import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DataProvider } from './contexts/DataContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import GlobalStyles from './components/Layout/GlobalStyles';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from './components/Layout/Navbar';
import PublicLayout from './components/Layout/PublicLayout';
import ResponsiveContainer from './components/Layout/ResponsiveContainer';
import CookieConsent from './components/Layout/CookieConsent';
import Home from './pages/Home';
import Features from './pages/Features';
import Pricing from './pages/Pricing';
import About from './pages/About';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Courses from './pages/Courses';
import AssignmentDetail from './pages/AssignmentDetail';
import Calendar from './pages/Calendar';
import Account from './pages/Account';
import Profile from './pages/Profile';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import Support from './pages/Support';
// import AI from './pages/AI';
import Flashcards from './pages/Flashcards';
import AdminSettings from './components/Admin/AdminSettings';
import { prefetchPricingPlans } from './services/pricingPlans';
import './App.css';

// Component to update document title based on current route
const DocumentTitle = () => {
  const location = useLocation();

  useEffect(() => {
    const pathToTitle = {
      '/': 'Home',
      '/features': 'Features',
      '/pricing': 'Pricing',
      '/about': 'About',
      '/login': 'Login',
      '/register': 'Register',
      '/dashboard': 'Dashboard',
      '/assignments': 'Assignments',
      '/assignments/:courseId': 'Course Assignments',
      '/assignments/:courseId/:assignmentId': 'Assignment Details',
      '/calendar': 'Calendar',
      '/account': 'Account',
      '/profile': 'Profile',
      '/analytics': 'Analytics',
      '/settings': 'Settings',
      '/support': 'Support',
      // '/ai': 'AI Assistant',
      '/flashcards': 'Flashcards',
      '/admin': 'Admin Settings'
    };

    const pageTitle = pathToTitle[location.pathname] || 'PlanMySemester';
    document.title = `PlanMySemester - ${pageTitle}`;
  }, [location]);

  return null;
};

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, twoFactorPending } = useAuth();
  const location = useLocation();

  if (loading) {
    return null; // Or a loading spinner
  }

  // Allow access to login page if 2FA is pending
  if (twoFactorPending && location.pathname === '/login') {
    return children;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

const AppContent = () => {
  const { isAuthenticated, loading } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();

  useEffect(() => {
    // When authenticated, prevent the browser window from scrolling and keep scrolling inside the app Main container
    if (isAuthenticated) {
      document.body.classList.add('app--no-window-scroll');
    } else {
      document.body.classList.remove('app--no-window-scroll');
    }

    return () => {
      document.body.classList.remove('app--no-window-scroll');
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const pricingRoutes = new Set(['/', '/home', '/dashboard']);

    if (pricingRoutes.has(location.pathname)) {
      prefetchPricingPlans();
    }
  }, [location.pathname]);

  // Removed loading screen - let pages load immediately

  // Only show the dashboard navbar on protected pages.
  const publicPaths = ['/', '/home', '/features', '/pricing', '/about', '/login', '/register'];
  const showNavbar = isAuthenticated && !publicPaths.includes(location.pathname);

  if (loading) {
    return null; // Or a loading spinner
  }

  return (
    <StyledThemeProvider theme={theme}>
      <GlobalStyles />
      <DocumentTitle />
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <CookieConsent />
      {showNavbar && <Navbar />}
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Home />} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/about" element={<About />} />
        </Route>
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <ResponsiveContainer>
              <Dashboard />
            </ResponsiveContainer>
          </ProtectedRoute>
        } />
        <Route path="/assignments/:courseId/:assignmentId" element={
          <ProtectedRoute>
            <ResponsiveContainer>
              <AssignmentDetail />
            </ResponsiveContainer>
          </ProtectedRoute>
        } />
        <Route path="/assignments/:courseId" element={
          <ProtectedRoute>
            <ResponsiveContainer>
              <AssignmentDetail />
            </ResponsiveContainer>
          </ProtectedRoute>
        } />
        <Route path="/assignments" element={
          <ProtectedRoute>
            <ResponsiveContainer>
              <AssignmentDetail />
            </ResponsiveContainer>
          </ProtectedRoute>
        } />
        <Route path="/courses" element={
          <ProtectedRoute>
            <ResponsiveContainer>
              <Courses />
            </ResponsiveContainer>
          </ProtectedRoute>
        } />
        <Route path="/calendar" element={
          <ProtectedRoute>
            <ResponsiveContainer>
              <Calendar />
            </ResponsiveContainer>
          </ProtectedRoute>
        } />
        <Route path="/account" element={
          <ProtectedRoute>
            <ResponsiveContainer>
              <Account />
            </ResponsiveContainer>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <ResponsiveContainer>
              <Profile />
            </ResponsiveContainer>
          </ProtectedRoute>
        } />
        <Route path="/analytics" element={
          <ProtectedRoute>
            <ResponsiveContainer>
              <Analytics />
            </ResponsiveContainer>
          </ProtectedRoute>
        } />
        <Route path="/settings" element={
          <ProtectedRoute>
            <ResponsiveContainer>
              <Settings />
            </ResponsiveContainer>
          </ProtectedRoute>
        } />
        <Route path="/support" element={
          <ProtectedRoute>
            <ResponsiveContainer>
              <Support />
            </ResponsiveContainer>
          </ProtectedRoute>
        } />
        {/*
        <Route path="/ai" element={
          <ProtectedRoute>
            <ResponsiveContainer>
              <AI />
            </ResponsiveContainer>
          </ProtectedRoute>
        } />
        */}
        <Route path="/flashcards" element={
          <ProtectedRoute>
            <ResponsiveContainer>
              <Flashcards />
            </ResponsiveContainer>
          </ProtectedRoute>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            <ResponsiveContainer>
              <AdminSettings />
            </ResponsiveContainer>
          </ProtectedRoute>
        } />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </StyledThemeProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <DataProvider>
        <ThemeProvider>
          <Router>
            <AppContent />
          </Router>
        </ThemeProvider>
      </DataProvider>
    </AuthProvider>
  );
}

export default App;
